import React from "react";
import { Box, Typography, Container, Grid, Button, Card, Paper, Chip } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import HomeFooter from "../HomeFooter";
import Navbar from "../Navbar";

const Pathway = () => {
    const navigate = useNavigate();

    const productFeatures = [
        {
            title: "About Pathway",
            subtitle: "Smart Shuttle Bus System",
            image: "p1.png",
            description: `Pathway is an innovative shuttle bus request system designed to revolutionize transportation within terminals. Utilizing cutting-edge technology, we make requesting rides seamless and efficient through multiple intuitive channels.`,
            features: [
                "QR Code Scanning: Instant ride requests via terminal QR codes",
                "Mobile App: Request rides directly from your smartphone",
                "Ticket Booking Screens: One-touch requests at strategic locations"
            ],
            icon: <InfoIcon />,
            color: "#1d4ed8",
            gradient: "linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)"
        },
        {
            title: "Key Benefits",
            subtitle: "Why Choose Pathway",
            image: "p2.jpg",
            description: `Experience unparalleled efficiency and safety with our comprehensive transportation solution designed for modern terminals.`,
            features: [
                "Reduced wait times with optimized routing",
                "Enhanced passenger safety with real-time tracking",
                "Increased operational efficiency by 40%",
                "24/7 system monitoring and support"
            ],
            icon: <SpeedIcon />,
            color: "#059669",
            gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)"
        },
        {
            title: "Safety & Security",
            subtitle: "Comprehensive Monitoring",
            image: "p3.jpg",
            description: `Our integrated security system ensures the highest standards of safety for all passengers and transportation providers.`,
            features: [
                "Real-time driver location monitoring",
                "Automated incident reporting system",
                "Route compliance verification",
                "Safety officer dashboard with analytics"
            ],
            icon: <SecurityIcon />,
            color: "#dc2626",
            gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
        },
        {
            title: "GPS Tracking",
            subtitle: "Real-Time Location Intelligence",
            image: "p4.jpg",
            description: `Advanced GPS technology provides precise tracking and monitoring capabilities for optimal transportation management.`,
            features: [
                "Cloud-based GPS software integration",
                "Real-time shuttle location tracking",
                "10-inch Android monitoring displays",
                "Satellite signal optimization"
            ],
            icon: <LocationOnIcon />,
            color: "#7c3aed",
            gradient: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)"
        },
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const cardHoverVariants = {
        hover: {
            y: -15,
            transition: {
                duration: 0.3,
                ease: "easeInOut"
            }
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f0f4f8" }}> {/* Changed to light background */}
            <Navbar />

            {/* Hero Section - LEFT ALIGNED */}
            <Box
                sx={{
                    py: { xs: 6, md: 10 },
                    px: { xs: 3, md: 6, lg: 10, xl: 14 },
                    background: "linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)", // Changed to light background
                    color: "#0f172a", // Changed to dark text
                    borderRadius: { xs: 0, md: 4 },
                    mx: { xs: 0, md: 4 },
                    mt: 9,
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "radial-gradient(circle at 20% 80%, rgba(69, 81, 190, 0.1) 0%, transparent 50%)", // Changed to blue accent
                    }
                }}
            >
                <Container maxWidth={false} sx={{ position: "relative", zIndex: 2 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                textAlign: "left",
                            }}
                        >
                            {/* Logo + Title in Same Row */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: { xs: 1.5, md: 2.5 },
                                    mb: 3,
                                }}
                            >
                                {/* Logo */}
                                <Box
                                    sx={{
                                        width: { xs: 65, md: 95 },
                                        height: { xs: 65, md: 95 },
                                        borderRadius: 2,

                                        bgcolor: "rgba(255,255,255,0.7)", // Light background

                                        backdropFilter: "blur(10px)",

                                        border: "1px solid rgba(0,0,0,0.1)",

                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",

                                        overflow: "hidden",

                                        flexShrink: 0,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/images/p1.png"
                                        alt="Pathway Logo"
                                        sx={{
                                            width: "100%",
                                            height: "100%",

                                            objectFit: "contain",

                                            transform: "scale(1.55)",

                                            display: "block",
                                        }}
                                    />
                                </Box>

                                {/* Title */}
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 900,

                                        fontSize: {
                                            xs: "2.3rem",
                                            sm: "3rem",
                                            md: "3.5rem",
                                            lg: "4rem",
                                        },

                                        fontFamily: "'Poppins', sans-serif",

                                        textTransform: "uppercase",

                                        letterSpacing: {
                                            xs: 1,
                                            md: 2,
                                        },

                                        color: "#4551be", // Changed to blue

                                        lineHeight: 1.1,
                                    }}
                                >
                                    Pathway
                                </Typography>
                            </Box>

                            {/* Subtitle */}
                            <Typography
                                variant="h5"
                                sx={{
                                    mb: 4,
                                    maxWidth: 800,
                                    fontWeight: 500,
                                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                                    opacity: 0.9,
                                    lineHeight: 1.6,
                                    color: "#475569", // Changed to dark grey
                                    mx: 0,
                                }}
                            >
                                Revolutionizing terminal transportation with smart, efficient and safe shuttle bus solutions
                            </Typography>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* Features Grid */}
            <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 } }}>
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    {productFeatures.map((item, index) => (
                        <motion.div key={index} variants={itemVariants}>
                            <Grid
                                container
                                spacing={{ xs: 4, md: 6 }}
                                alignItems="center"
                                sx={{
                                    mb: { xs: 8, md: 12 },
                                    flexDirection: {
                                        xs: "column",
                                        md: index % 2 === 0 ? "row" : "row-reverse",
                                    },
                                }}
                            >
                                {/* Image Section */}
                                <Grid item xs={12} md={6}>
                                    <motion.div
                                        variants={cardHoverVariants}
                                        whileHover="hover"
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                borderRadius: 4,
                                                overflow: "hidden",
                                                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.08)", // Lighter shadow
                                                position: "relative",
                                                "&::before": {
                                                    content: '""',
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: item.gradient,
                                                    opacity: 0.1,
                                                    zIndex: 1,
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    zIndex: 2,
                                                    overflow: "hidden",
                                                    "&::after": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: `linear-gradient(45deg, ${item.color}15, transparent)`,
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={`/images/${item.image}`}
                                                    alt={item.title}
                                                    style={{
                                                        width: "100%",
                                                        height: "auto",
                                                        objectFit: "cover",
                                                        aspectRatio: "4/3",
                                                        display: "block",
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 20,
                                                    left: 20,
                                                    bgcolor: "white",
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: 3,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
                                                    zIndex: 3
                                                }}
                                            >
                                                <Box sx={{ color: item.color, fontSize: 30 }}>
                                                    {item.icon}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </motion.div>
                                </Grid>

                                {/* Content Section */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            px: { xs: 0, md: index % 2 === 0 ? 4 : 0 },
                                            pr: { md: index % 2 === 0 ? 0 : 4 }
                                        }}
                                    >
                                        <Chip
                                            label={item.subtitle}
                                            sx={{
                                                bgcolor: `${item.color}15`,
                                                color: item.color,
                                                fontWeight: 600,
                                                mb: 3,
                                                px: 1,
                                                height: 32,
                                                fontSize: "0.85rem"
                                            }}
                                        />

                                        <Typography
                                            variant="h3"
                                            sx={{
                                                fontWeight: 800,
                                                color: "#1e293b", // Dark text
                                                mb: 3,
                                                fontSize: { xs: "1.75rem", md: "2.25rem" },
                                                position: "relative",
                                                "&::after": {
                                                    content: '""',
                                                    position: "absolute",
                                                    bottom: -12,
                                                    left: 0,
                                                    width: 60,
                                                    height: 4,
                                                    background: item.gradient,
                                                    borderRadius: 2,
                                                }
                                            }}
                                        >
                                            {item.title}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: { xs: "1rem", md: "1.1rem" },
                                                lineHeight: 1.8,
                                                color: "#64748b", // Dark grey text
                                                mb: 4,
                                                textAlign: { xs: "center", md: "left" },
                                            }}
                                        >
                                            {item.description}
                                        </Typography>

                                        <Box sx={{ mt: 2 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#1e293b", // Dark text
                                                    mb: 2.5,
                                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1
                                                }}
                                            >
                                                Key Features:
                                            </Typography>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                {item.features.map((feature, idx) => (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 2,
                                                            p: 2,
                                                            borderRadius: 2,
                                                            bgcolor: "#f8fafc",
                                                            border: "1px solid #e2e8f0",
                                                            transition: "all 0.3s ease",
                                                            "&:hover": {
                                                                bgcolor: "#ffffff",
                                                                borderColor: item.color,
                                                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                                                                transform: "translateX(5px)"
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: "50%",
                                                                bgcolor: item.color,
                                                                mt: 1,
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: { xs: "0.95rem", md: "1rem" },
                                                                color: "#475569", // Dark grey text
                                                                lineHeight: 1.7,
                                                            }}
                                                        >
                                                            {feature}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </motion.div>
                    ))}
                </motion.div>
            </Container>

            <HomeFooter />
        </Box>
    );
};

export default Pathway;